import { Box, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { ROUTE_KEYS } from '@/navigation/constants';
import { COLORS } from '@/styles/tokens/colors';
import { WorkflowTrackerKind } from '@/types/schema';

import { ClientOnboardingDeliverableModalBodyProps } from '../../types';
import { AssetIntegrationPromptCard } from '../components/AssetIntegrationPromptCard/AssetIntegrationPromptCard';
import { useHouseholdConnectionDetails } from '../components/AssetIntegrationPromptCard/useHouseholdConnectionDetails';
import {
  GenericDocumentProcessingConfig,
  GenericDocumentProcessingModalBody,
} from './GenericDocumentProcessingModalBody';

function CreateBalanceSheetModalBodyInner({
  onClose,
  onChildPhaseChange,
}: ClientOnboardingDeliverableModalBodyProps) {
  const { showIntegrationPromptCard } = useHouseholdConnectionDetails();
  const { uploadedFiles, selectedExistingDocuments, isUploading } =
    useMultiDocumentUploaderContext();

  const hideNonDocumentUploader = useMemo(() => {
    return (
      !isEmpty(uploadedFiles) ||
      !isEmpty(selectedExistingDocuments) ||
      isUploading
    );
  }, [isUploading, selectedExistingDocuments, uploadedFiles]);

  const additionalUploadContent = !hideNonDocumentUploader ? (
    <>
      {showIntegrationPromptCard && (
        <>
          <AssetIntegrationPromptCard />
          <Box textAlign="center" my={2}>
            <Typography variant="body-xs">or</Typography>
          </Box>
        </>
      )}
      <Stack direction="column" gap={0.5}>
        <Typography variant="title-l" color={COLORS.NAVY['700']}>
          Create with document(s)
        </Typography>
        <Typography variant="body-s" color={COLORS.GRAY['500']}>
          Luminary AI will extract and suggest balance sheet data
        </Typography>
      </Stack>
    </>
  ) : null;

  const config: GenericDocumentProcessingConfig = {
    allowSelectExistingDocuments: false,
    subheader:
      'Luminary AI will process each document and identify individuals, organizations, entities, and sub-trusts for you to review.',
    workflowTrackerKind: WorkflowTrackerKind.DocumentExtraction,
    useAIWorkflow: true,
    processingHeader: (documentNoun: string) => `Processing ${documentNoun}...`,
    processingSubheader: (
      <>
        We are processing your documents and will notify you when balance sheet
        suggestions are ready for review.
        <strong> This process may take up to several minutes.</strong>
      </>
    ),
    successHeader: 'Success! Balance sheet suggestions ready',
    successSubheader: 'Your balance sheet suggestions are ready for review',
    errorMessage:
      'We failed to extract balance sheet suggestions from these documents. Please upload new documents or re-select the previous document to try again.',
    completionButtonLabel: 'Continue to balance sheet',
    onCompletionClick: (
      navigate: ReturnType<typeof useNavigateToRoute>['navigate'],
      householdId: string
    ) => {
      navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_BALANCE_SHEET, {
        householdId,
      });
    },
    additionalUploadContentAbove: additionalUploadContent,
  };

  return (
    <GenericDocumentProcessingModalBody
      onClose={onClose}
      onChildPhaseChange={onChildPhaseChange}
      config={config}
    />
  );
}

export function CreateBalanceSheetModalBody(
  props: ClientOnboardingDeliverableModalBodyProps
) {
  return (
    <MultiDocumentUploaderProvider>
      <CreateBalanceSheetModalBodyInner {...props} />
    </MultiDocumentUploaderProvider>
  );
}
